import React, { useState, useContext, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Card,
    CardContent,
    Stack,
    Divider,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from 'axios';
import { AppContext } from "../context/appContext";

const CheckoutModal = ({ open, onClose, onConfirm }) => {
    const { userData } = useContext(AppContext);
    const [editing, setEditing] = useState(false);
    const [address, setAddress] = useState(null);
    const [phone, setPhone] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("COD");
    const [loadingUser, setLoadingUser] = useState(true);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const isMobile = useMediaQuery("(max-width:768px)");

    // Helper: check if address is empty object
    const isEmptyAddress = (obj) => !obj || Object.keys(obj).length === 0;

    // Sync address and phone once userData loads
    useEffect(() => {
        if (userData) {
            if (isEmptyAddress(userData.address)) {
                setAddress(null);
            } else {
                setAddress(userData.address);
            }
            setPhone(userData.phone || "");
            setLoadingUser(false);
        }
    }, [userData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") setPhone(value);
        setAddress((prev) => ({ ...prev, [name]: value }));
    };

    const handleSaveAddress = async () => {
        // Save address & phone of user
        try {
            const res = await axios.post(`${backendUrl}/api/user/update-user-details`, { address, phone }, { withCredentials: true });
            if (res.data.success) {
                setEditing(false);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleConfirm = async () => {
        if (address === null || address === undefined) {
            toast.error("Please Add A Delivery Address!");
            return false;
        }
        setConfirmLoading(true);
        onConfirm(paymentMethod);
    };

    const renderAddressView = () => (
        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 1, mb: 2 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Delivery Address</Typography>
                    <Button
                        size="small"
                        startIcon={<Edit fontSize="small" />}
                        onClick={() => setEditing(true)}
                    >
                        Edit
                    </Button>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body1">{address?.fullName}</Typography>
                <Typography variant="body2">{phone}</Typography>
                <Typography variant="body2">{address?.street}</Typography>
                <Typography variant="body2">
                    {address?.city}, {address?.state} {address?.zip}
                </Typography>
            </CardContent>
        </Card>
    );

    const renderAddressForm = () => (
        <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Add / Edit Address</Typography>
            <TextField
                label="Full Name"
                name="fullName"
                value={address?.fullName || ""}
                onChange={handleInputChange}
                fullWidth
            />
            <TextField
                label="Phone Number"
                name="phone"
                value={phone}
                onChange={handleInputChange}
                fullWidth
            />
            <TextField
                label="Street Address"
                name="street"
                value={address?.street || ""}
                onChange={handleInputChange}
                fullWidth
            />
            <TextField
                label="City"
                name="city"
                value={address?.city || ""}
                onChange={handleInputChange}
                fullWidth
            />
            <TextField
                label="State"
                name="state"
                value={address?.state || ""}
                onChange={handleInputChange}
                fullWidth
            />
            <TextField
                label="Zip Code"
                name="zip"
                value={address?.zip || ""}
                onChange={handleInputChange}
                fullWidth
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setEditing(false)}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSaveAddress}>
                    Save Address
                </Button>
            </Stack>
        </Box>
    );

    // Prevent UI flicker while loading user data
    if (loadingUser) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogContent
                    sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
                >
                    <CircularProgress color="error" />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            className="checkout-modal"
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, p: 1 } }}
        >
            <DialogTitle sx={{ fontWeight: 600 }}>Checkout Details</DialogTitle>

            <DialogContent dividers>
                {/* Address Section */}
                {address && !editing ? renderAddressView() : editing ? renderAddressForm() : null}

                {/* Add new address button if no address */}
                {!address && !editing && (
                    <Button
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 3 }}
                        onClick={() => setEditing(true)}
                    >
                        + Add New Address
                    </Button>
                )}

                {/* Payment Method */}
                <Box mt={2}>
                    <Typography variant="h6" gutterBottom>
                        Payment Method
                    </Typography>
                    <RadioGroup
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                        <FormControlLabel value="COD" control={<Radio />} label="Cash on Delivery" />
                        <FormControlLabel value="Card" control={<Radio />} label="Credit / Debit Card" />
                    </RadioGroup>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={confirmLoading}>Cancel</Button>
                <Button variant="contained" color="error" onClick={handleConfirm} disabled={confirmLoading}>
                    {confirmLoading ? (
                        <>
                            <CircularProgress size={22} sx={{ color: "white", mr: 1 }} />
                            Processing...
                        </>
                    ) : (
                        "Confirm & Continue"
                    )}

                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CheckoutModal;